import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Grid,
    Autocomplete,
    Chip,
    Typography,
    CircularProgress,
} from '@mui/material';

// Sugestoes pre-definidas por app_connector_provider (baseado nos providers cadastrados
// em tb_app_connector_provider — Jira, Bitrix24, STAYS). O usuario ainda pode digitar
// livre no Autocomplete (freeSolo=true). Sem provider selecionado, cai no "generic".
const SUGGESTIONS_BY_PROVIDER = {
    1: ['issue_id', 'issue_key', 'issue_url', 'project_key'], // Jira
    2: ['reservation_id', 'reservation_key', 'client_id'], // STAYS
    3: ['task_id', 'deal_id', 'lead_id', 'contact_id', 'company_id'], // Bitrix24
};
const SUGGESTIONS_GENERIC = ['external_id', 'external_key', 'external_url', 'issue_id', 'ticket_id', 'record_id'];

const ENTITY_TYPE_SUGGESTIONS = ['issue', 'task', 'deal', 'lead', 'contact', 'company', 'reservation', 'record'];

// Dialog do CRUD de um mapping unico. Modo unificado create/edit — se `mapping` prop
// vier populado, edita; se nulo, cria. `providers` e o catalogo do backend (opcional).
class WebhookResponseMappingDialog extends Component {
    constructor(props) {
        super(props);
        this.state = this.buildInitialState(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentDidUpdate(prevProps) {
        // Ao abrir, resetar o estado com base no mapping atual (create ou edit).
        if (this.props.open && !prevProps.open) {
            this.setState(this.buildInitialState(this.props));
        }
    }

    buildInitialState(props) {
        const m = props.mapping || {};
        return {
            appconnectorproviderid: m.appconnectorproviderid ?? null,
            jsonpath: m.jsonpath ?? '',
            externalkeyname: m.externalkeyname ?? '',
            externalentitytype: m.externalentitytype ?? '',
            description: m.description ?? '',
            active: m.active !== false, // default true
            submitting: false,
        };
    }

    handleChange(name, value) {
        this.setState({ [name]: value });
    }

    handleSubmit(e) {
        if (e) e.preventDefault();
        const { jsonpath, externalkeyname, externalentitytype } = this.state;
        if (!jsonpath.trim() || !externalkeyname.trim() || !externalentitytype.trim()) {
            return;
        }
        this.setState({ submitting: true });
        const payload = {
            appConnectorProviderId: this.state.appconnectorproviderid || null,
            jsonPath: jsonpath.trim(),
            externalKeyName: externalkeyname.trim(),
            externalEntityType: externalentitytype.trim(),
            description: this.state.description?.trim() || null,
            active: this.state.active,
        };
        Promise.resolve(this.props.onSubmit(payload))
            .finally(() => this.setState({ submitting: false }));
    }

    getKeySuggestions() {
        const providerId = this.state.appconnectorproviderid;
        if (providerId && SUGGESTIONS_BY_PROVIDER[providerId]) {
            return [...SUGGESTIONS_BY_PROVIDER[providerId], ...SUGGESTIONS_GENERIC.filter(s => !SUGGESTIONS_BY_PROVIDER[providerId].includes(s))];
        }
        return SUGGESTIONS_GENERIC;
    }

    render() {
        const { intl, open, onClose, mapping, providers = [] } = this.props;
        const { appconnectorproviderid, jsonpath, externalkeyname, externalentitytype, description, active, submitting } = this.state;
        const isEdit = !!(mapping && mapping.id);
        const canSubmit = jsonpath.trim() && externalkeyname.trim() && externalentitytype.trim() && !submitting;

        const providerOptions = providers.map(p => ({ id: p.id, label: p.name }));
        const selectedProvider = providerOptions.find(p => p.id === appconnectorproviderid) || null;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 2 } }}
            >
                <DialogTitle>
                    {intl.formatMessage({
                        id: isEdit ? 'webhook.response.mapping.edit' : 'webhook.response.mapping.new'
                    })}
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 20px' }}>
                        <form onSubmit={this.handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                                        {intl.formatMessage({ id: 'webhook.response.mapping.help' })}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Autocomplete
                                        options={providerOptions}
                                        getOptionLabel={(opt) => opt.label || ''}
                                        value={selectedProvider}
                                        onChange={(_, v) => this.handleChange('appconnectorproviderid', v ? v.id : null)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={intl.formatMessage({ id: 'webhook.response.mapping.provider' })}
                                                variant="outlined"
                                                helperText={intl.formatMessage({ id: 'webhook.response.mapping.provider.help' })}
                                            />
                                        )}
                                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        required
                                        variant="outlined"
                                        fullWidth
                                        label={intl.formatMessage({ id: 'webhook.response.mapping.jsonpath' })}
                                        value={jsonpath}
                                        onChange={(e) => this.handleChange('jsonpath', e.target.value)}
                                        placeholder="$.id"
                                        helperText={intl.formatMessage({ id: 'webhook.response.mapping.jsonpath.help' })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        options={this.getKeySuggestions()}
                                        value={externalkeyname}
                                        onInputChange={(_, v) => this.handleChange('externalkeyname', v || '')}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                variant="outlined"
                                                label={intl.formatMessage({ id: 'webhook.response.mapping.key' })}
                                                placeholder="issue_id"
                                                helperText={intl.formatMessage({ id: 'webhook.response.mapping.key.help' })}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        options={ENTITY_TYPE_SUGGESTIONS}
                                        value={externalentitytype}
                                        onInputChange={(_, v) => this.handleChange('externalentitytype', v || '')}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                variant="outlined"
                                                label={intl.formatMessage({ id: 'webhook.response.mapping.entitytype' })}
                                                placeholder="issue"
                                                helperText={intl.formatMessage({ id: 'webhook.response.mapping.entitytype.help' })}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label={intl.formatMessage({ id: 'description' })}
                                        value={description}
                                        onChange={(e) => this.handleChange('description', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={active}
                                                onChange={(e) => this.handleChange('active', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={intl.formatMessage({ id: 'webhook.response.mapping.active' })}
                                    />
                                </Grid>
                            </Grid>
                        </form>
                    </div>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} disabled={submitting}>
                        {intl.formatMessage({ id: 'cancel' })}
                    </Button>
                    <Button
                        onClick={this.handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!canSubmit}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {intl.formatMessage({ id: isEdit ? 'save' : 'add' })}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

WebhookResponseMappingDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    mapping: PropTypes.object,
    providers: PropTypes.array,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(WebhookResponseMappingDialog);
